/**
 * Játékos-rendszer: mozgás, kamera, élet/páncél, készlet, felvétel, használat.
 *
 * A számok a `parbeszed/05-X-gdd.md` 6. pontjából valók.
 *
 * Gazda: X (C készítette a működő alapot — tiéd, vidd specre)
 */

import * as THREE from 'three'
import { secs, type Ctx, type System, type Actor, type KeyColor } from '../core/contracts'
import { clamp, damp, DEG, wrapAngle } from '../core/math'
import type { World } from '../world/world'
import type { Input } from '../input/input'

// --- GDD 6. pont: mozgásszámok -------------------------------------------
export const PLAYER = {
  radius: 0.42,
  height: 1.8,
  eyeHeight: 1.62,
  maxSpeed: 9.5,
  accel: 70,
  friction: 18,
  airControl: 35,
  gravity: 30,
  jumpSpeed: 8.5,
  stepHeight: 0.45,
  safeFall: 3.0,
  fallDamagePerUnit: 10,
  maxFallDamage: 60,
  pitchLimit: 80 * DEG,
  bobFrequency: 8,
  bobAmplitude: 0.035,
  bobRoll: 0.7 * DEG,
  useRange: 2.6,
  pickupRange: 1.25,
  /** Sebzés után ennyi tickig nem indulhat újra a regeneráció. */
  regenDelay: secs(6),
  /** Élet/másodperc, a regeneráció aktív szakaszában. */
  regenRate: 3.5,
  /** A regeneráció felső határa; medkit továbbra is a maxHealthig gyógyít. */
  regenCap: 100,
} as const

export type AmmoType = 'bullet' | 'shell' | 'rocket' | 'cell'

export interface Inventory {
  health: number
  maxHealth: number
  armor: number
  maxArmor: number
  ammo: Record<AmmoType, number>
  ammoMax: Record<AmmoType, number>
  weapons: Set<string>
  keys: Set<KeyColor>
}

export function createInventory(): Inventory {
  return {
    health: 100,
    maxHealth: 100,
    armor: 0,
    maxArmor: 100,
    ammo: { bullet: 50, shell: 0, rocket: 0, cell: 0 },
    ammoMax: { bullet: 200, shell: 40, rocket: 20, cell: 100 },
    weapons: new Set(['fist', 'pistol']),
    keys: new Set(),
  }
}

const _fwd = new THREE.Vector3()
const _right = new THREE.Vector3()
const _tmp = new THREE.Vector3()
const _eye = new THREE.Vector3()

export class PlayerSystem implements System {
  readonly name = 'player'
  readonly inventory: Inventory = createInventory()

  private yaw = 0
  private pitch = 0
  private bobPhase = 0
  private bobAmount = 0
  private fallStartY = 0
  private wasOnGround = true
  private hazardAccum = 0
  private painCooldown = 0
  private regenDelayRemaining = PLAYER.regenDelay
  private regenActive = false
  private deathUnsubscribe: (() => void) | null = null
  /** Az utolsó két tick nézésváltozása — a fegyver lendületéhez. */
  lastLookDelta = { x: 0, y: 0 }

  constructor(
    private readonly world: World,
    private readonly input: Input,
  ) {}

  init(ctx: Ctx): void {
    const p = ctx.actors.player
    this.yaw = p.yaw
    this.pitch = 0
    this.fallStartY = p.pos.y
    // Enélkül a képernyőn egyáltalán nincs fegyver.
    ctx.view.equip('pistol')

    // A páncél-elnyelést a sebzési belépési pont innen olvassa.
    this.deathUnsubscribe?.()
    this.deathUnsubscribe = ctx.events.on('death', ({ actor }) => {
      if (actor.kind === 'player') {
        ctx.state = 'dead'
        ctx.fx.sound('playerDeath', null, 1)
      }
    })
  }

  dispose(): void {
    this.deathUnsubscribe?.()
    this.deathUnsubscribe = null
  }

  fixedUpdate(ctx: Ctx): void {
    const p = ctx.actors.player
    if (!p.alive) {
      this.updateDeathCamera(ctx, p)
      return
    }

    const dt = ctx.dt
    const inp = ctx.input

    // --- Nézés ---
    this.yaw = wrapAngle(this.yaw + inp.lookYaw)
    this.pitch = clamp(this.pitch + inp.lookPitch, -PLAYER.pitchLimit, PLAYER.pitchLimit)
    this.lastLookDelta.x = inp.lookYaw
    this.lastLookDelta.y = inp.lookPitch
    p.yaw = this.yaw

    // --- Kívánt mozgásirány a nézéshez képest ---
    _fwd.set(Math.sin(this.yaw), 0, Math.cos(this.yaw))
    // Jobbra mutató vektor = előre × fel. +Z felé nézve ez -X (jobbsodrású
    // rendszerben), ezért az előjelek NEM a naiv (cos, -sin) alak szerintiek.
    _right.set(-Math.cos(this.yaw), 0, Math.sin(this.yaw))
    const wishX = _fwd.x * inp.moveZ + _right.x * inp.moveX
    const wishZ = _fwd.z * inp.moveZ + _right.z * inp.moveX
    const wishLen = Math.hypot(wishX, wishZ)

    // --- Vízszintes gyorsulás és súrlódás ---
    //
    // FONTOS a sorrend és a szétválasztás. Ha a súrlódás a teljes
    // sebességvektorra hatna MENET KÖZBEN is, akkor a végsebességet nem a
    // `maxSpeed`, hanem a `accel / friction` hányados határozná meg
    // (70/18 ≈ 3.9 egység/s) — vagyis a GDD 9.5-ös célsebessége sosem
    // állna be. Ezért:
    //   - a KÍVÁNT irányba egyenletesen gyorsítunk a célsebességig
    //     (0 → 9.5 kb. 9.5/70 ≈ 136 ms alatt, ez a GDD „140 ms"-a),
    //   - a súrlódás csak a kívánt irányra MERŐLEGES komponensre és a
    //     bemenet nélküli lassulásra hat (exp(-18·t): ~220 ms alatt megáll,
    //     ez a GDD „220 ms"-a).
    // Így mindkét GDD-szám pontosan teljesül, és a kanyar feszes marad.
    const accel = p.onGround ? PLAYER.accel : PLAYER.airControl
    const frictionK = p.onGround ? Math.exp(-PLAYER.friction * dt) : 1

    if (wishLen > 1e-4) {
      const nx = wishX / wishLen
      const nz = wishZ / wishLen

      // Felbontás: a kívánt irányba eső és az arra merőleges komponens.
      const along = p.vel.x * nx + p.vel.z * nz
      let perpX = p.vel.x - nx * along
      let perpZ = p.vel.z - nz * along

      // A merőleges „sodródást" a súrlódás elnyeli — ettől lesz éles a kanyar.
      perpX *= frictionK
      perpZ *= frictionK

      const target = PLAYER.maxSpeed * wishLen
      let newAlong = along
      if (along < target) {
        newAlong = Math.min(target, along + accel * dt)
      } else if (p.onGround) {
        // Túlsebesség (pl. lift vagy robbanás után): a súrlódás lehúzza.
        newAlong = Math.max(target, along * frictionK)
      }

      p.vel.x = nx * newAlong + perpX
      p.vel.z = nz * newAlong + perpZ
    } else if (p.onGround) {
      // Nincs bemenet: teljes súrlódás, exponenciális lassulás.
      p.vel.x *= frictionK
      p.vel.z *= frictionK
      if (Math.hypot(p.vel.x, p.vel.z) < 0.05) {
        p.vel.x = 0
        p.vel.z = 0
      }
    }

    // --- Ugrás ---
    if (this.input.consumeJump() && p.onGround) {
      p.vel.y = PLAYER.jumpSpeed
      p.onGround = false
      this.fallStartY = p.pos.y
    }

    // --- Vízszintes mozgatás + lépcsőzés ---
    const before = p.pos.y
    this.world.moveActor(p, p.vel.x * dt, p.vel.z * dt, PLAYER.stepHeight)

    const floor = this.world.floorAt(p.pos.x, p.pos.z, p.roomId)
    if (p.onGround || (floor > p.pos.y && floor - p.pos.y <= PLAYER.stepHeight)) {
      if (floor > p.pos.y) {
        // Automatikus fellépés — a kamera simán követi, nem ugrik.
        p.pos.y = floor
        p.onGround = true
      }
    }
    void before

    // --- Gravitáció és padló ---
    p.vel.y -= PLAYER.gravity * dt
    p.pos.y += p.vel.y * dt

    const ceil = this.world.ceilAt(p.pos.x, p.pos.z, p.roomId)
    if (p.pos.y + p.height > ceil) {
      p.pos.y = ceil - p.height
      if (p.vel.y > 0) p.vel.y = 0
    }

    if (p.pos.y <= floor + 1e-4) {
      if (!this.wasOnGround) this.applyFallDamage(ctx, p)
      p.pos.y = floor
      p.vel.y = 0
      p.onGround = true
    } else {
      if (this.wasOnGround) this.fallStartY = Math.max(this.fallStartY, p.pos.y)
      p.onGround = false
    }
    if (p.onGround) this.fallStartY = p.pos.y
    this.wasOnGround = p.onGround

    // --- Kamera: szemmagasság + fejbólogatás ---
    const speed = Math.hypot(p.vel.x, p.vel.z)
    const targetBob = p.onGround && speed > 1 ? Math.min(1, speed / PLAYER.maxSpeed) : 0
    this.bobAmount = damp(this.bobAmount, targetBob, 0.02, dt)
    this.bobPhase += dt * Math.PI * 2 * PLAYER.bobFrequency * Math.max(0.4, targetBob)

    ctx.camera.pos.set(
      p.pos.x,
      p.pos.y + PLAYER.eyeHeight + Math.sin(this.bobPhase) * PLAYER.bobAmplitude * this.bobAmount,
      p.pos.z,
    )
    ctx.camera.yaw = this.yaw
    ctx.camera.pitch = this.pitch
    ctx.camera.roll = Math.cos(this.bobPhase * 0.5) * PLAYER.bobRoll * this.bobAmount

    // --- Használat (ajtó, kapcsoló) ---
    if (inp.usePressed) this.tryUse(ctx, p)

    // --- Padló-veszély (savas tér) ---
    const hazard = this.world.hazardAt(p.pos.x, p.pos.z, p.roomId)
    if (hazard > 0 && p.onGround) {
      this.hazardAccum += hazard * dt
      if (this.hazardAccum >= 1) {
        const dmg = Math.floor(this.hazardAccum)
        this.hazardAccum -= dmg
        ctx.actors.damage(p, dmg, null, null, 'hazard')
        ctx.fx.sound('hazardBurn', null, 0.35)
      }
    } else {
      this.hazardAccum = 0
    }

    // --- Tárgyfelvétel ---
    this.collectPickups(ctx, p)

    // --- Titkos szobák ---
    this.world.checkSecrets(p)

    if (this.painCooldown > 0) this.painCooldown -= dt

    this.updateRegeneration(ctx, p)
    this.syncHud(ctx)

  }

  private updateDeathCamera(ctx: Ctx, p: Actor): void {
    // Halálkor a kamera lassan a padlóra süllyed és megdől — a Doom
    // ikonikus záróképe, és jelzi, hogy vége.
    const floor = this.world.floorAt(p.pos.x, p.pos.z, p.roomId)
    const target = floor + 0.35
    ctx.camera.pos.set(p.pos.x, damp(ctx.camera.pos.y, target, 0.02, ctx.dt), p.pos.z)
    ctx.camera.roll = damp(ctx.camera.roll, 0.42, 0.06, ctx.dt)
    ctx.camera.pitch = damp(ctx.camera.pitch, -0.12, 0.06, ctx.dt)
    this.syncHud(ctx)
  }

  private applyFallDamage(ctx: Ctx, p: Actor): void {
    const drop = this.fallStartY - p.pos.y
    if (drop <= PLAYER.safeFall) return
    const dmg = Math.min(PLAYER.maxFallDamage, Math.round((drop - PLAYER.safeFall) * PLAYER.fallDamagePerUnit))
    if (dmg > 0) {
      ctx.actors.damage(p, dmg, null, null, 'hazard')
      ctx.fx.shake(0.3)
    }
  }

  private tryUse(ctx: Ctx, p: Actor): void {
    _eye.set(p.pos.x, p.pos.y + PLAYER.eyeHeight, p.pos.z)
    _tmp.set(Math.sin(this.yaw), 0, Math.cos(this.yaw))
    const res = this.world.tryUse(_eye, _tmp, PLAYER.useRange, this.inventory.keys)
    switch (res) {
      case 'opened':
        ctx.fx.sound('doorOpen', _eye, 0.7)
        break
      case 'locked':
        ctx.fx.sound('doorLocked', _eye, 0.8)
        ctx.fx.message('Zárva — kulcs kell hozzá.')
        break
      case 'switched':
        ctx.fx.sound('doorOpen', _eye, 0.6)
        break
      case 'none':
        break
      case 'exit':
        break
    }
  }

  private pickupBuf: Actor[] = []

  private collectPickups(ctx: Ctx, p: Actor): void {
    const found = ctx.actors.query(p.pos.x, p.pos.z, p.radius + PLAYER.pickupRange, null, this.pickupBuf)
    for (const item of found) {
      if (item.kind !== 'pickup' || !item.alive) continue
      // Függőleges ellenőrzés: a fejed fölötti erkélyen lévő tárgy ne ugorjon be.
      if (Math.abs(item.pos.y - p.pos.y) > 2.2) continue
      if (this.applyPickup(ctx, item.type)) {
        item.alive = false
        ctx.events.emit('pickup', { type: item.type, by: p })
        ctx.actors.remove(item.id)
      }
    }
  }

  /** @returns true, ha a tárgy elfogyott (felvettük). */
  applyPickup(ctx: Ctx, type: string): boolean {
    const inv = this.inventory

    const ammoGrant = (kind: AmmoType, amount: number, label: string): boolean => {
      if (inv.ammo[kind] >= inv.ammoMax[kind]) return false
      inv.ammo[kind] = Math.min(inv.ammoMax[kind], inv.ammo[kind] + amount)
      ctx.fx.sound('pickupItem', null, 0.55)
      ctx.fx.message(label)
      return true
    }

    switch (type) {
      case 'medkitSmall':
        if (inv.health >= inv.maxHealth) return false
        inv.health = Math.min(inv.maxHealth, inv.health + 25)
        ctx.actors.player.health = inv.health
        ctx.fx.sound('pickupItem', null, 0.6)
        ctx.fx.screenFlash(0x2a8a3a, 0.14)
        ctx.fx.message('Elsősegélycsomag +25')
        return true

      case 'medkitLarge':
        if (inv.health >= inv.maxHealth) return false
        inv.health = Math.min(inv.maxHealth, inv.health + 50)
        ctx.actors.player.health = inv.health
        ctx.fx.sound('pickupItem', null, 0.7)
        ctx.fx.screenFlash(0x2a8a3a, 0.2)
        ctx.fx.message('Orvosi láda +50')
        return true

      case 'armor':
        if (inv.armor >= inv.maxArmor) return false
        inv.armor = Math.min(inv.maxArmor, inv.armor + 50)
        ctx.fx.sound('pickupItem', null, 0.7)
        ctx.fx.screenFlash(0x64c4cf, 0.16)
        ctx.fx.message('Páncéllemez +50')
        return true

      case 'bullets':
        return ammoGrant('bullet', 20, 'Lőszer +20')
      case 'shells':
        return ammoGrant('shell', 8, 'Sörét +8')
      case 'rockets':
        return ammoGrant('rocket', 4, 'Rakéta +4')
      case 'cells':
        return ammoGrant('cell', 24, 'Energiacella +24')

      case 'keyBlue':
      case 'keyYellow':
      case 'keyRed': {
        const color = type.slice(3).toLowerCase() as KeyColor
        inv.keys.add(color)
        ctx.events.emit('keyTaken', { color })
        ctx.fx.sound('pickupKey', null, 0.9)
        ctx.fx.screenFlash(color === 'red' ? 0xff3b1f : color === 'blue' ? 0x64c4cf : 0xe4b02c, 0.25)
        ctx.fx.message(`${color === 'red' ? 'Vörös' : color === 'blue' ? 'Kék' : 'Sárga'} kulcskártya`)
        return true
      }

      default:
        // Fegyver-felvétel: ha ismerjük a nevét, a készletbe kerül.
        if (type.startsWith('weapon:')) {
          const id = type.slice(7)
          const isNew = !inv.weapons.has(id)
          inv.weapons.add(id)
          ctx.fx.sound('pickupWeapon', null, 0.85)
          ctx.fx.message(isNew ? `Új fegyver: ${id.toUpperCase()}` : 'Lőszer a fegyverből')
          return true
        }
        return false
    }
  }

  private syncHud(ctx: Ctx): void {
    const inv = this.inventory
    const hud = ctx.hud
    hud.health = inv.health
    hud.maxHealth = inv.maxHealth
    hud.armor = inv.armor
    hud.keys = [...inv.keys]
    // Minden tickben kiírjuk: a HUD nem egy korábbi regenerációs állapotot
    // pulzáltat, hanem pontosan azt, amelyik ebben a szimulációs tickben él.
    hud.regenActive = this.regenActive
  }

  private updateRegeneration(ctx: Ctx, p: Actor): void {
    const cap = Math.min(PLAYER.regenCap, this.inventory.maxHealth)
    if (this.regenDelayRemaining > 0) {
      this.regenDelayRemaining--
      this.regenActive = false
      return
    }
    if (this.inventory.health >= cap) {
      this.regenActive = false
      return
    }
    if (!this.regenActive) {
      this.regenActive = true
      // Meglévő, rövid felvételhang halk változata; külön regenerációs hang
      // később az audio-gazda által cserélhető.
      ctx.fx.sound('pickupItem', null, 0.18, 1.35)
    }
    this.inventory.health = Math.min(cap, this.inventory.health + PLAYER.regenRate * ctx.dt)
    p.health = this.inventory.health
  }

  /** A sebzési belépési pont páncél-hookja. */
  get armorHook() {
    return {
      get: (): number => this.inventory.armor,
      consume: (n: number): void => {
        this.inventory.armor = Math.max(0, this.inventory.armor - n)
      },
    }
  }

  /** Az `ActorStore.damage` a `health`-et írja; ide szinkronizáljuk vissza. */
  onDamaged(ctx: Ctx): void {
    this.inventory.health = ctx.actors.player.health
    this.regenDelayRemaining = PLAYER.regenDelay
    this.regenActive = false
    if (this.painCooldown <= 0) {
      ctx.fx.sound('playerPain', null, 0.85)
      this.painCooldown = 0.35
    }
    ctx.fx.screenFlash(0x8c0808, 0.32)
    ctx.fx.shake(0.28)
  }

}
